/**
 * When is it safe to change the layout of a page someone is already reading?
 *
 * The home feed has two pieces of layout that can only be decided client-side:
 * the personal rails (~1,900 px inserted below the first rail) and the pitch
 * collapse (~5,000 px removed below the feed, `body[data-returning]`). Both land
 * after hydration, which on a slow connection is seconds after first paint.
 *
 * Every CLS measurement of this page until now — R131, R132, R133, R133b — was
 * taken with the page **parked at scroll 0**, where both changes happen off-screen
 * and cost nothing. Measured again with the reader where a reader actually is:
 *
 * | signed in, 1280, scrolled to… | CLS |
 * |---|---|
 * | 0 (as previously measured) | 0.000 |
 * | 900 — the feed, insertion point mid-viewport | **0.845** |
 * | 2,500 — past the insertion point | 0.000 |
 * | 4,000 — inside the pitch that is about to collapse | **0.860** |
 * | 900, throttled 400 kbps / 4× CPU, real wheel scroll at 1.5 s | **0.873** |
 *
 * The 2,500 row is the tell: when the change happens entirely **above** the
 * viewport the browser's own scroll anchoring absorbs it and nothing is recorded.
 * When it happens entirely **below** the viewport nothing visible moves either. The
 * damage is only done when the change point is *inside* the viewport, where
 * anchoring has no anchor to hold.
 *
 * So the fix is not to shift less, it is to **wait**: hold the change until its
 * insertion point is out of the reader's viewport. A reader of a feed scrolls, and
 * the first scroll makes it safe.
 *
 * **Rejected, with numbers.** Compensating the scroll instead (`window.scrollBy` by
 * the height that appeared, in a `ResizeObserver`) is the textbook answer and it is
 * worse here: layout-instability scores the *impacted fraction of the viewport*, so
 * moving the reader deeper into the block that is still growing widens the impact
 * rather than cancelling it. Measured at 1280: 0.845 → **1.044** at scroll 900,
 * 0.860 → **1.001** at 4,000. Reserving three rail placeholders instead of one, so
 * the final swap would be small, also lost: **0.737** against 0.397 on the same
 * run, because total CLS tracks the size of the *first* insertion. (W14 R140)
 */

/** How long a change may be held back before it happens regardless. */
export const INSERT_GRACE_MS = 10_000;

export interface InsertSafety {
  /** The change point's offset from the top of the viewport, px (viewport coords). */
  top: number;
  viewportHeight: number;
}

/**
 * Can the layout change at `top` without moving anything the reader can see?
 *
 * True above the viewport (scroll anchoring absorbs it — measured 0.000) and at or
 * below the fold (nothing visible moves). False inside the viewport, which is the
 * only case that costs anything.
 */
export function isSafeToInsertAt({ top, viewportHeight }: InsertSafety): boolean {
  if (!Number.isFinite(top) || !Number.isFinite(viewportHeight) || viewportHeight <= 0) {
    // Unmeasurable: don't hold content hostage to a broken measurement.
    return true;
  }
  return top <= 0 || top >= viewportHeight;
}
